import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  Button,
  CheckIcon,
  Flex,
  Form,
  Input,
  Tooltip,
  WarningIcon,
  toast,
  Notice,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import ConfirmPasswordModal from '../../shared/components/ConfirmPasswordModal'
import style from '../ProfileSettings.module.css'
import { isValidEmail } from '../utils/isValidEmail'

export const ACCOUNT_INITIATE_BACKUP_EMAIL_VERIFICATION =
  graphql(/* GraphQL */ `
    mutation AccountInitiateBackupEmailVerification($isBackupEmail: Boolean) {
      accountInitiateEmailVerification(isBackupEmail: $isBackupEmail) {
        ... on AccountInitiateEmailVerificationResponse {
          success
        }
        ... on AccountInitiateEmailVerificationError {
          userFriendlyMessage
        }
      }
    }
  `)

export const INITIATE_UPDATE_EMAIL = graphql(/* GraphQL */ `
  mutation InitiateUpdateBackupEmail($input: InitiateUpdateEmailInput!) {
    initiateUpdateEmail(input: $input) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on UnexpectedError {
        message
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

export const BackupEmailBanner = (): JSX.Element | null => {
  const { account } = useAccount()
  const [sent, setSent] = useState(false)
  const [initiateBackupEmailVerification, { data, error }] = useMutation(
    ACCOUNT_INITIATE_BACKUP_EMAIL_VERIFICATION,
  )
  const banner = useDismissableBanner('add-backup-email-notice')
  const hasVerifiedBackup =
    account?.backupEmail && account?.hasVerifiedBackupEmail

  const hasBackupButNotVerified =
    account?.backupEmail && !account?.hasVerifiedBackupEmail

  // Only the "add a backup email" prompt is dismissible; the verify reminder
  // that follows once an email is added must always be shown.
  const isAddPrompt = !hasBackupButNotVerified

  const noticeText = hasBackupButNotVerified
    ? 'Verify your backup email: An email has been sent to your inbox to verify your email address.'
    : 'Add a backup email to your account as an additional security measure'

  const showActionButton = !sent && hasBackupButNotVerified

  useEffect(() => {
    if (
      data?.accountInitiateEmailVerification?.__typename ===
        'AccountInitiateEmailVerificationResponse' &&
      data.accountInitiateEmailVerification.success
    ) {
      toast.success(
        'An email has been sent to your inbox to verify your email address.',
      )
    } else if (
      data?.accountInitiateEmailVerification?.__typename ===
      'AccountInitiateEmailVerificationError'
    ) {
      setSent(false)
      toast.error(data.accountInitiateEmailVerification.userFriendlyMessage)
    }
    if (error) {
      setSent(false)
      toast.error(
        'An error occurred while sending the verification email. Please try again later.',
      )
    }
  }, [data, error])

  if (!account?.id || hasVerifiedBackup || (isAddPrompt && !banner.isActive)) {
    return null
  }

  const handleResendVerification = (): void => {
    void initiateBackupEmailVerification({
      variables: {
        isBackupEmail: true,
      },
    })
  }

  return (
    <Notice variant="info" onDismiss={isAddPrompt ? banner.dismiss : undefined}>
      <Notice.Text>{noticeText}</Notice.Text>
      {showActionButton && (
        <Notice.Actions>
          <Button
            size="small"
            variant="primary"
            onClick={handleResendVerification}
          >
            Re-send Verification Email
          </Button>
        </Notice.Actions>
      )}
    </Notice>
  )
}

const BackupEmail = (props: { triggerAccountFetch: boolean }): JSX.Element => {
  const { triggerAccountFetch } = props
  const [initiateUpdateEmail] = useMutation(INITIATE_UPDATE_EMAIL)
  const { account, refetchAccount } = useAccount()
  const currentBackupEmail = account?.backupEmail
  const hasVerifiedBackupEmail = account?.hasVerifiedBackupEmail

  const [newEmail, setNewEmail] = useState(currentBackupEmail)
  const [dirty, setDirty] = useState(false)
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null)
  const [polling, setPolling] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [waitingForEmailConfirmation, setWaitingForEmailConfirmation] =
    useState<string | null>(null)

  useEffect(() => {
    if (triggerAccountFetch && refetchAccount) {
      void refetchAccount()
    }
  }, [triggerAccountFetch, refetchAccount])

  useEffect(() => {
    setInvalidMessage(null)
    if (waitingForEmailConfirmation) {
      // don't set dirty if we're waiting for the email
      // to be confirmed
      return
    }
    if (
      newEmail !== undefined &&
      newEmail !== null &&
      newEmail !== (currentBackupEmail ?? '')
    ) {
      setDirty(true)
    } else {
      setDirty(false)
    }
  }, [waitingForEmailConfirmation, newEmail, currentBackupEmail])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (polling) {
      interval = setInterval(() => {
        if (refetchAccount) {
          void refetchAccount()
        }
        if (currentBackupEmail === waitingForEmailConfirmation) {
          setWaitingForEmailConfirmation(null)
          setNewEmail(currentBackupEmail)
          setDirty(false)
          setInvalidMessage(null)
          setPolling(false)
        }
      }, 2000)
    } else {
      if (interval) clearInterval(interval)
    }

    return (): void => {
      if (interval) clearInterval(interval)
    }
  }, [polling, refetchAccount, currentBackupEmail, waitingForEmailConfirmation])

  const handleSubmit = async (password: string): Promise<void> => {
    if (!account?.id || !password || !newEmail) {
      toast.error('Unable to update email. Please try again.')
      return
    }

    const result = await initiateUpdateEmail({
      variables: {
        input: {
          accountId: account?.id,
          password,
          isBackupEmail: true,
          newEmail,
        },
      },
    })

    if (result.data?.initiateUpdateEmail?.__typename !== 'EmptySuccess') {
      toast.error(
        'An error occurred while updating your email. Please try again later.',
      )
      setIsPasswordModalOpen(false)
    } else {
      toast.success(
        'Email sent! Head to your inbox and click the verification link.',
      )
      setPolling(true)
      setWaitingForEmailConfirmation(newEmail)
      setIsPasswordModalOpen(false)
    }
  }

  const getInvalidMessage = (): string | null => {
    if (!newEmail || !isValidEmail(newEmail)) {
      return 'Please enter a valid email address.'
    }
    if (newEmail.toLowerCase() === account?.email?.toLowerCase()) {
      return 'Please use an email that is different from your primary email'
    }
    return null
  }

  let suffix = null
  if (account?.backupEmail) {
    suffix = hasVerifiedBackupEmail ? (
      <Tooltip content="Email is verified">
        <CheckIcon
          data-testid="verified-email-icon"
          color="currentColor"
          style={{ color: 'var(--color-border-success)' }}
        />
      </Tooltip>
    ) : (
      <Tooltip content="Email is not verified">
        <WarningIcon
          data-testid="warning-email-icon"
          color="currentColor"
          style={{ color: 'var(--color-border-warning)' }}
        />
      </Tooltip>
    )
  }

  return (
    <Form name="addBackupEmailForm" className={style.fullWidth}>
      <Form.Field
        name="newBackupEmail"
        controlId="newBackupEmail"
        className={style.fullWidth}
        invalid={!!invalidMessage}
      >
        <Form.Label>Backup Email</Form.Label>
        <Flex gap="xs" align="end" className={style.fullWidth}>
          <Form.Control>
            <Input
              className={style.flexInput}
              defaultValue={currentBackupEmail ?? ''}
              onChange={(event): void => setNewEmail(event.target.value)}
              onBlur={(): void => {
                if (dirty) {
                  setInvalidMessage(getInvalidMessage())
                }
              }}
              suffix={suffix}
              autoComplete="email"
              disabled={waitingForEmailConfirmation !== null}
            />
          </Form.Control>
          <ConfirmPasswordModal
            open={isPasswordModalOpen}
            onOpenChange={(open) => {
              setIsPasswordModalOpen(open)
              if (!open) {
                setPolling(false)
              }
            }}
            description="To update your backup email, please enter your current password."
            onConfirm={(password): void => {
              void handleSubmit(password)
            }}
          >
            <Button
              size="medium"
              variant="secondary"
              disabled={!dirty || waitingForEmailConfirmation !== null}
              className={style.fixedWidthButton}
              onClick={(e): void => {
                const message = getInvalidMessage()
                if (message) {
                  toast.error(message)
                  setInvalidMessage(message)
                  e.preventDefault()
                  return
                }
                setIsPasswordModalOpen(true)
              }}
            >
              Save Changes
            </Button>
          </ConfirmPasswordModal>
        </Flex>
        <Form.Error>{invalidMessage}</Form.Error>
      </Form.Field>
    </Form>
  )
}

export default BackupEmail
